import React from 'react';
import {
  clientFieldFocusStyles,
  fontSans,
  headerSurfaceBg,
  pageGutter,
  primaryHeaderButtonStyle,
  secondaryHeaderButtonStyle
} from './requestChangesStyles';

function displayClientName(name) {
  return String(name || '').replace(/\s*\(V\d+\)\s*$/, '').trim();
}

function buildMetaLine(clientName, eventName, eventDate) {
  return [displayClientName(clientName), eventName, eventDate]
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join(' · ');
}

function DownloadIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function MaykerHeaderMark() {
  return (
    <img
      className="proposal-view-header-mark"
      src="/mayker_icon-black.png"
      alt=""
      aria-hidden="true"
      onError={(e) => {
        if (!e.target.src.includes('/assets/')) {
          e.target.src = '/assets/mayker_icon-black.png';
        } else if (!e.target.src.includes('cdn')) {
          e.target.src = 'https://cdn.jsdelivr.net/gh/MaykerCreative/mayker-proposals@main/public/mayker_icon-black.png';
        }
      }}
    />
  );
}

const utilityTextStyle = {
  background: 'none',
  border: 'none',
  padding: '4px 0',
  margin: 0,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 12,
  fontWeight: 400,
  letterSpacing: 0,
  textTransform: 'none',
  color: '#6b645c',
  fontFamily: fontSans,
  cursor: 'pointer',
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
  boxShadow: 'none',
  outline: 'none'
};

export default function ProposalViewHeader({
  clientName,
  eventName,
  eventDate,
  showPricingToggle = false,
  showDiscount = true,
  onToggleDiscount,
  onExportPdf,
  onRequestChanges,
  onAcceptProposal,
  acceptanceUiState,
  canReplayConfirmation = false,
  storedAcceptance = null,
  version,
  onBack,
  onLogout,
  pendingReview = false,
  onAboutProposal
}) {
  const metaLine = buildMetaLine(clientName, eventName, eventDate);
  const request = storedAcceptance && storedAcceptance.reapprovalRequest;
  const requestedVersion = request ? parseInt(request.requestedVersion, 10) : NaN;
  const currentVersion = parseInt(version, 10);
  const awaitingReapproval = Boolean(
    request
    && String(request.status || '').trim() === 'open'
    && Number.isFinite(requestedVersion)
    && Number.isFinite(currentVersion)
    && requestedVersion === currentVersion
  );
  const showAcceptButton = !pendingReview && (
    awaitingReapproval || canReplayConfirmation || acceptanceUiState === 'none'
  );
  const showAcceptedState = !pendingReview && !awaitingReapproval && acceptanceUiState === 'accepted' && !canReplayConfirmation;
  const showClientActions = !pendingReview;

  return (
    <div
      className="no-print proposal-view-header"
      style={{
        width: '100%',
        backgroundColor: headerSurfaceBg,
        borderBottom: '1px solid #eeeae3'
      }}
    >
      <style>{`
        ${clientFieldFocusStyles}
        .proposal-view-header-actions button:focus-visible,
        .proposal-view-header-utilities button:focus-visible,
        .proposal-view-header-back:focus-visible,
        .proposal-view-header-about:focus-visible {
          outline: 2px solid #2C2C2C;
          outline-offset: 2px;
        }
        .proposal-view-header-brand {
          display: flex;
          align-items: center;
          gap: 14px;
          min-width: 0;
          flex: 1 1 auto;
        }
        .proposal-view-header-mark {
          width: 28px;
          height: 28px;
          display: block;
          flex-shrink: 0;
        }
        .proposal-view-header-divider {
          width: 1px;
          height: 28px;
          background-color: #e8e0d4;
          flex-shrink: 0;
        }
        .proposal-view-header-about {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          border: 1px solid #d8d0c4;
          background: transparent;
          color: #8a8378;
          font-size: 11px;
          font-weight: 500;
          font-family: ${fontSans};
          line-height: 1;
          padding: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          flex-shrink: 0;
        }
        .proposal-view-header-about:hover {
          color: #2C2C2C;
          border-color: #c4bbb0;
        }
        @media screen and (min-width: 769px) and (max-width: 1024px) {
          [data-client-shareable-link="true"] .proposal-view-header-inner {
            padding-left: 24px !important;
            padding-right: 24px !important;
          }
        }
        @media screen and (max-width: 1100px) {
          .proposal-view-header-right {
            flex-wrap: wrap !important;
            justify-content: flex-end !important;
            row-gap: 12px !important;
          }
        }
        @media screen and (max-width: 768px) {
          [data-client-shareable-link="true"] .proposal-view-header-inner {
            padding-left: 16px !important;
            padding-right: 16px !important;
          }
          .proposal-view-header-inner {
            flex-wrap: wrap !important;
            align-items: flex-start !important;
            gap: 12px !important;
          }
          .proposal-view-header-brand {
            width: 100%;
          }
          .proposal-view-header-right {
            width: 100%;
            justify-content: space-between !important;
          }
          .proposal-view-header-actions {
            margin-left: auto;
          }
          .proposal-view-header-utilities button:not(.proposal-view-header-about),
          .proposal-view-header-actions button {
            width: auto !important;
            flex: 0 0 auto !important;
          }
          .proposal-view-header-actions button {
            height: 36px !important;
            padding: 8px 12px !important;
          }
          .proposal-view-header-utilities button[role="switch"] {
            width: 32px !important;
            height: 18px !important;
            padding: 0 !important;
          }
        }
      `}</style>
      <div
        className="proposal-view-header-inner"
        style={{
          width: '100%',
          boxSizing: 'border-box',
          padding: `12px ${pageGutter}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 28
        }}
      >
        <div className="proposal-view-header-brand">
          <MaykerHeaderMark />
          <span className="proposal-view-header-divider" aria-hidden="true" />
          <div className="proposal-view-header-copy" style={{ minWidth: 0, flex: '1 1 auto' }}>
            {onBack ? (
              <button
                type="button"
                className="proposal-view-header-back"
                onClick={onBack}
                style={{
                  ...utilityTextStyle,
                  fontSize: 11,
                  color: '#8a8378',
                  padding: 0,
                  marginBottom: 6
                }}
              >
                ← Dashboard
              </button>
            ) : null}
            <div style={{
              fontFamily: fontSans,
              fontSize: 12,
              fontWeight: 600,
              color: '#2C2C2C',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              lineHeight: 1.2
            }}>
              Your Project
            </div>
            {metaLine ? (
              <p
                className="proposal-view-header-meta"
                style={{
                margin: '5px 0 0',
                fontFamily: fontSans,
                fontSize: 13,
                color: '#6b645c',
                lineHeight: 1.4,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {metaLine}
              </p>
            ) : null}
          </div>
        </div>

        <div
          className="proposal-view-header-right"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 28,
            flexShrink: 0
          }}
        >
          <div
            className="proposal-view-header-utilities"
            style={{ display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0, flexWrap: 'nowrap' }}
          >
            {showPricingToggle ? (
              <div
                className="no-print"
                data-discount-toggle="wrapper"
                role="group"
                aria-label="Discount line on proposal and PDF"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
              >
                <span style={{
                  fontSize: 11,
                  fontFamily: fontSans,
                  fontWeight: 400,
                  color: '#8a8378',
                  lineHeight: 1,
                  whiteSpace: 'nowrap'
                }}>
                  Show Preferred Pricing
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={showDiscount}
                  aria-label={showDiscount ? 'Hide preferred pricing' : 'Show preferred pricing'}
                  onClick={onToggleDiscount}
                  style={{
                    width: 32,
                    height: 18,
                    borderRadius: 9,
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    flexShrink: 0,
                    position: 'relative',
                    backgroundColor: showDiscount ? '#8a8378' : '#d8d0c4',
                    transition: 'background-color 0.2s ease',
                    boxShadow: 'none'
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      top: 2,
                      left: showDiscount ? 16 : 2,
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      backgroundColor: '#fff',
                      transition: 'left 0.2s ease'
                    }}
                  />
                </button>
              </div>
            ) : null}
            {showClientActions ? (
              <button type="button" className="no-print" onClick={onExportPdf} style={utilityTextStyle}>
                <DownloadIcon />
                Export PDF
              </button>
            ) : null}
            {onAboutProposal ? (
              <button
                type="button"
                className="no-print proposal-view-header-about"
                onClick={onAboutProposal}
                aria-label="About this project"
                title="About this project"
              >
                ?
              </button>
            ) : null}
            {onLogout ? (
              <button type="button" onClick={onLogout} style={{ ...utilityTextStyle, fontSize: 11, color: '#8a8378' }}>
                Sign out
              </button>
            ) : null}
          </div>

          <div
            className="proposal-view-header-actions"
            style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, flexWrap: 'nowrap' }}
          >
            {pendingReview ? (
              <span style={{
                fontSize: 11,
                color: '#8a8378',
                fontFamily: fontSans,
                whiteSpace: 'nowrap',
                letterSpacing: '0.04em',
                textTransform: 'uppercase'
              }}>
                Under Review
              </span>
            ) : null}
            {awaitingReapproval && showClientActions ? (
              <span style={{
                fontSize: 11,
                color: '#8a8378',
                fontFamily: fontSans,
                whiteSpace: 'nowrap',
                marginRight: 4
              }}>
                This revised proposal requires acceptance
              </span>
            ) : null}
            {showClientActions ? (
              <button type="button" onClick={onRequestChanges} style={secondaryHeaderButtonStyle}>
                Request Changes
              </button>
            ) : null}
            {showAcceptedState ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3, marginLeft: 2 }}>
                <span style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: '#2C2C2C',
                  fontFamily: fontSans,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase'
                }}>
                  Proposal Accepted
                </span>
                {storedAcceptance?.agreementEmail ? (
                  <span style={{ fontSize: 11, color: '#8a8378', fontFamily: fontSans }}>
                    Agreement sent to {storedAcceptance.agreementEmail}
                  </span>
                ) : null}
              </div>
            ) : null}
            {showAcceptButton ? (
              <button type="button" onClick={onAcceptProposal} style={primaryHeaderButtonStyle}>
                {awaitingReapproval ? 'Accept Revised Proposal' : 'Accept Proposal'}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
